import { useIonModal } from '@ionic/react'
import { useEffect, useState } from 'react'

import {
  MyMonitoringAccountUnitCard,
  MyMonitoringUnitModifyModal
} from '@apps-common/apps/portal/pages/my-monitoring/pages/details/components'
import { UnitListCardSmSkeleton } from '@apps-common/apps/portal/pages/units/components'
import { NoResultsModalAddCard } from '@apps-common/components/common'
import { PageSubtitle } from '@apps-common/components/common/portal-title'
import { ButtonRoundSm } from '@apps-common/components/elements'
import { VertSpacerXl } from '@apps-common/components/layout'
import { ModalButton, PortalUnitSelectModal } from '@apps-common/components/modals'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS } from '@apps-common/stores'
import { IMyMonitoringAccountUnitItemModel } from '@apps-common/types/portal/my-monitoring'

interface IProps {
  userId: number
  loading?: boolean
  disabled?: boolean
  units?: IMyMonitoringAccountUnitItemModel[]
}

export const MyMonitoringAccountInternalUnits: React.FC<IProps> = ({ userId, loading, disabled, units }) => {
  const [newUnitId, setNewUnitId] = useState<string>()

  const modalProps = {
    exclusions: {
      ids: units?.map?.((d) => d.unitId),
      component: (
        <div className='jost-light text-[0.85em] text-orange-500 flex items-center justify-between grow px-[10px] py-[5px] border-[1px] border-orange-500 rounded-full'>
          Device already added <commonIcons.toastWarning className='text-[1.2em]' />
        </div>
      )
    },
    filterDefaults: {
      state: {
        ...DEFAULT_PORTAL_UNITS_FILTER_STORE_PROPS,
        hasNoActivation: true,
        isActivated: true
      },
      filter: {
        hideStatus: true,
        hideIsActivated: true,
        hideReset: true
      }
    }
  }

  const modalCallback = (d: any) => {
    if (d.unitId) {
      setNewUnitId(d.unitId)
    }
  }

  const [present, dismiss] = useIonModal(MyMonitoringUnitModifyModal, {
    componentProps: {
      userId,
      unitId: newUnitId
    },
    onDismiss: () => {
      setNewUnitId(undefined)
      dismiss(null, ModalDismiss.Confirm)
    },
    enableBackdropDismiss: true
  })

  useEffect(() => {
    if (newUnitId) {
      present({
        enterAnimation: modalSlideUpEnterAnimation,
        leaveAnimation: modalSlideUpLeaveAnimation,
        backdropDismiss: true
      })
    }
  }, [newUnitId])

  return (
    <div className='p-3 pb-3 bg-surface-2 border border-border rounded-3xl'>
      <div className='flex items-center justify-between pl-2'>
        <PageSubtitle>Devices</PageSubtitle>
        <ModalButton
          component={PortalUnitSelectModal}
          disabled={loading || disabled}
          componentProps={modalProps}
          confirmCallback={modalCallback}
          enterAnimation={modalSlideUpEnterAnimation}
          leaveAnimation={modalSlideUpLeaveAnimation}
          backdropDismiss
        >
          <ButtonRoundSm type='div' className='action-success'>
            <commonIcons.add />
          </ButtonRoundSm>
        </ModalButton>
      </div>

      <VertSpacerXl />
      <div className='grid sm:grid-cols-2 gap-[20px]'>
        {(loading || !units) &&
          Array.from(Array(2).keys()).map((k, i) => (
            <UnitListCardSmSkeleton
              key={`my-monitoring-account-unit-skeleton-${k}`}
              index={i}
              bgClasses='bg-skeleton border border-skeleton min-w-full sm:min-w-[200px] lg:min-w-[250px]'
              textClasses=''
              iconClasses=''
              bgSecondaryClasses='bg-skeleton-hi'
              textSecondaryClasses=''
              bgTertiaryClasses=''
            />
          ))}
        {!loading && units && units?.length === 0 && (
          <NoResultsModalAddCard
            title='No devices'
            message='No devices have been added to this account.'
            classes1='!bg-surface sm:col-span-2'
            disabled={loading || !!disabled}
            callback={modalCallback}
            Modal={PortalUnitSelectModal}
            modalProps={modalProps}
          />
        )}
        {!loading &&
          units &&
          units?.length > 0 &&
          units?.map((d) => (
            <MyMonitoringAccountUnitCard key={`my-monitoring-account-unit-${d.unitId}`} userId={userId} disabled={disabled} unit={d} />
          ))}
      </div>
    </div>
  )
}
